import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { ContentCopy, Edit } from '@mui/icons-material';
import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { ChangeRequest } from '@/api/bol/models';
import { getActionTooltip } from '@/hooks';
import { createTraceDisplayProps } from '@/utils/grafanaUtils';
import { formatDateTime, renderChip } from '@/utils/datagridFormatters';

const renderCopyableField = (value: string, onCopy: (text: string) => void) => (
  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1, minWidth: 0 }}>
    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
      {value}
    </Typography>
    <Tooltip title="Copy">
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopy(value); }} sx={{ flexShrink: 0 }}>
        <ContentCopy fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

const renderTraceField = (value: string, onCopy: (text: string) => void) => {
  const traceValue = value === '-' ? '' : value;
  const traceProps = createTraceDisplayProps(traceValue);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1, minWidth: 0 }}>
      <Tooltip title={traceProps.title}>
        <Typography
          variant="caption"
          aria-disabled={!traceProps.onClick}
          sx={{
            fontFamily: 'monospace',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
            ...traceProps.style,
            '&:hover': traceProps.onClick ? { color: 'primary.dark' } : undefined
          }}
          onClick={traceProps.onClick ? (e) => {
            e.stopPropagation();
            traceProps.onClick?.();
          } : undefined}
        >
          {value}
        </Typography>
      </Tooltip>
      {value !== '-' && (
        <Tooltip title="Copy Trace">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopy(value); }} sx={{ flexShrink: 0 }}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export const createChangeRequestColumns = (
  onCopy: (text: string) => void,
  onPatch: (changeRequest: ChangeRequest) => void,
  canUpdate: boolean,
  isPatching: boolean
): GridColDef[] => [
  {
    field: 'id',
    headerName: 'Change Request ID',
    width: 200,
    renderCell: (params) => renderCopyableField(params.value, onCopy)
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    flex: 1,
    minWidth: 160,
    valueGetter: (_, row) => row.createdAt ? formatDateTime(row.createdAt) : '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (params) => renderChip('changeRequestStatus', params.value),
  },
  {
    field: 'updatedAt',
    headerName: 'Last Modified',
    flex: 1,
    minWidth: 160,
    valueGetter: (_, row) => row.updatedAt ? formatDateTime(row.updatedAt) : '-',
  },
  {
    field: 'logisticsObjectRevision',
    headerName: 'Target Revision',
    width: 130,
    renderCell: (params) => (
      <Chip label={params.value.toString()} size="small" color="primary" variant="outlined" />
    ),
  },
  {
    field: 'requestorMemberIdentifier',
    headerName: 'Requestor',
    flex: 1,
    minWidth: 180,
    valueGetter: (_, row) => row.requestorMemberIdentifier || '-',
    renderCell: (params) => (
      <Typography variant="caption" sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', px: 1, py: 0.25, borderRadius: 0.5 }}>
        {params.value}
      </Typography>
    )
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    minWidth: 150,
    valueGetter: (_, row) => row.description || 'No description',
  },
  {
    field: 'creationTraceparent',
    headerName: 'Creation Trace',
    flex: 1,
    minWidth: 100,
    valueGetter: (_, row) => row.creationTraceparent || '-',
    renderCell: (params) => renderTraceField(params.value, onCopy)
  },
  {
    field: 'updateTraceparent',
    headerName: 'Update Trace',
    flex: 1,
    minWidth: 100,
    valueGetter: (_, row) => row.updateTraceparent || '-',
    renderCell: (params) => renderTraceField(params.value, onCopy)
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 100,
    getActions: (params) => {
      if (params.row.status !== 'PENDING') return [];
      
      const action = (
        <GridActionsCellItem
          key="patch"
          icon={<Edit />}
          label="Accept/Reject"
          onClick={() => onPatch(params.row)}
          disabled={isPatching || !canUpdate}
          showInMenu={false}
        />
      );
      
      return canUpdate ? [action] : [
        <Tooltip key="patch-tooltip" title={getActionTooltip('updateLogisticsObject')}>
          <span>{action}</span>
        </Tooltip>
      ];
    },
  },
];
